import React, { useEffect, useRef, useState } from 'react';

/**
 * CursorTrail - Optimized particle trail following cursor
 * - Respects lightweight mode toggle
 * - Throttled particle creation for performance
 * - Reduced shadowBlur for GPU efficiency
 * - Lower particle limit to prevent lag
 */
const CursorTrail = () => {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const mouse = useRef({ x: 0, y: 0 });
  const lastParticleTime = useRef(0);
  const rafRef = useRef(null);

  // Respect lightweight mode
  const [enabled, setEnabled] = useState(() => {
    const saved = localStorage.getItem('lightweightMode');
    // Hide when lightweight mode is ON (default ON so trail is hidden by default)
    const lightweight = saved === null ? true : saved === 'true';
    return !lightweight;
  });

  // Listen for lightweight mode changes
  useEffect(() => {
    const handleLightweightModeChange = (e) => {
      setEnabled(!e.detail.enabled);
    };
    window.addEventListener('lightweightModeChange', handleLightweightModeChange);
    return () => window.removeEventListener('lightweightModeChange', handleLightweightModeChange);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Mushroom-themed colors (pre-computed for performance)
    const colors = ['#7c3aed', '#ec4899', '#fbbf24', '#a78bfa', '#f472b6'];

    class Particle {
      constructor(x, y) {
        this.x = x;
        this.y = y;
        this.size = Math.random() * 6 + 3; // Slightly smaller
        this.speedX = (Math.random() - 0.5) * 1.5; // Slower movement
        this.speedY = (Math.random() - 0.5) * 1.5;
        this.life = 1;
        this.decay = Math.random() * 0.025 + 0.015; // Faster decay
        this.color = colors[Math.floor(Math.random() * colors.length)];
        // Decide once at creation whether to draw emoji or circle
        this.isEmoji = Math.random() > 0.75;
        this.emoji = this.isEmoji ? (Math.random() > 0.5 ? '🍄' : '✨') : null;
      }

      update() {
        this.x += this.speedX;
        this.y += this.speedY;
        this.life -= this.decay;
        this.size *= 0.97;
      }

      draw() {
        if (this.life <= 0) return;
        ctx.globalAlpha = this.life;

        if (this.isEmoji) {
          ctx.font = `${this.size}px Arial`;
          ctx.fillText(this.emoji, this.x, this.y);
        } else {
          ctx.fillStyle = this.color;
          // Reduced shadowBlur for better performance
          ctx.shadowBlur = 8;
          ctx.shadowColor = this.color;
          ctx.beginPath();
          ctx.arc(this.x, this.y, this.size / 2, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0; // Reset immediately
        }
      }

      isDead() {
        return this.life <= 0 || this.size <= 0.5;
      }
    }

    // Throttled mouse move handler - only create particles every 16ms (~60fps)
    const handleMouseMove = (e) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;

      const now = performance.now();
      if (now - lastParticleTime.current > 16) {
        // Create only 1 particle per frame instead of 2
        particles.current.push(new Particle(e.clientX, e.clientY));
        lastParticleTime.current = now;
      }
    };

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    function animate() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      particles.current = particles.current.filter(particle => {
        particle.update();
        particle.draw();
        return !particle.isDead();
      });

      // Lower particle limit for better performance
      if (particles.current.length > 50) {
        particles.current = particles.current.slice(-50);
      }

      rafRef.current = requestAnimationFrame(animate);
    }

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9999,
        willChange: 'transform', // GPU acceleration hint
      }}
    />
  );
};

export default CursorTrail;

